/**
 * Clarifying-question quiz: when a request is too vague, the assistant may reply
 * with a short set of multiple-choice questions instead of a final answer. The
 * model writes it as a fenced ```quiz block holding JSON; the app turns that into
 * an interactive card, and the chosen answers go back as the next user message.
 */

export const QUIZ_FENCE = 'quiz';

export const QUIZ_LIMITS = {
  maxQuestions: 5,
  minOptions: 2,
  maxOptions: 8,
  maxQuestionLength: 240,
  maxOptionLength: 120,
  maxLabelLength: 40,
} as const;

export interface QuizQuestion {
  id: string;
  text: string;
  options: string[];
  /** Several options may apply (checkboxes) instead of exactly one (radio buttons). */
  multiple: boolean;
}

export interface Quiz {
  questions: QuizQuestion[];
  /** Model-written label of the submit button ("Подобрать сервисы"), if any. */
  submitLabel: string | null;
}

export type QuizSegment =
  | { type: 'text'; text: string }
  | { type: 'quiz'; quiz: Quiz }
  /** A quiz block that is still being written (streaming): show a placeholder, never raw JSON. */
  | { type: 'quiz-pending' };

/** Chosen options per question id. */
export type QuizAnswers = Record<string, string[]>;

/** Added to the system prompt of normal chat turns so the model knows the format. */
export const QUIZ_SYSTEM_HINT = [
  'Clarifying questions. If the request lacks details that would noticeably change your answer',
  '(choosing between options, planning, recommendations that depend on the user\'s situation), you MAY ask',
  `up to ${QUIZ_LIMITS.maxQuestions} short multiple-choice questions instead of answering right away.`,
  'Write one short intro sentence, then a fenced block exactly like this:',
  '```' + QUIZ_FENCE,
  '{"questions":[{"id":"q1","text":"Question?","options":["Option A","Option B","Option C"],"multiple":false}],"submitLabel":"Short button label"}',
  '```',
  'Rules: write everything in the user\'s language; 2-5 options per question; use "multiple":true only when',
  'several options can apply; at most one quiz per reply and nothing after the block; "id" values are short and',
  'unique; "submitLabel" is a few words describing the result the user will get. Do NOT use a quiz when the',
  'request is already clear or simple, for casual conversation, or when the user has just answered such',
  'questions: the answers arrive as the next message, then give the full answer without asking again.',
].join(' ');

/** Validates the JSON of a quiz block; returns null for anything malformed so it is never half-rendered. */
export function parseQuizBlock(json: string): Quiz | null {
  let data: unknown;
  try {
    data = JSON.parse(json);
  } catch {
    return null;
  }
  if (!data || typeof data !== 'object') return null;
  const root = data as { questions?: unknown; submitLabel?: unknown };
  if (!Array.isArray(root.questions)) return null;
  if (root.questions.length < 1 || root.questions.length > QUIZ_LIMITS.maxQuestions) return null;

  const ids = new Set<string>();
  const questions: QuizQuestion[] = [];
  for (const [index, raw] of root.questions.entries()) {
    if (!raw || typeof raw !== 'object') return null;
    const item = raw as { id?: unknown; text?: unknown; options?: unknown; multiple?: unknown };
    const text = typeof item.text === 'string' ? item.text.trim() : '';
    if (!text || text.length > QUIZ_LIMITS.maxQuestionLength) return null;
    if (!Array.isArray(item.options)) return null;
    const options = item.options.map((option) => (typeof option === 'string' ? option.trim() : ''));
    if (
      options.length < QUIZ_LIMITS.minOptions ||
      options.length > QUIZ_LIMITS.maxOptions ||
      options.some((option) => !option || option.length > QUIZ_LIMITS.maxOptionLength) ||
      new Set(options).size !== options.length
    ) {
      return null;
    }
    const id = typeof item.id === 'string' && item.id.trim() ? item.id.trim().slice(0, 40) : `q${index + 1}`;
    if (ids.has(id)) return null;
    ids.add(id);
    questions.push({ id, text, options, multiple: item.multiple === true });
  }

  const label = typeof root.submitLabel === 'string' ? root.submitLabel.trim() : '';
  return {
    questions,
    submitLabel: label && label.length <= QUIZ_LIMITS.maxLabelLength ? label : null,
  };
}

const OPEN = '```' + QUIZ_FENCE;

/**
 * Splits an assistant reply into prose and quiz blocks. A malformed block stays
 * in the text as an ordinary code block (visible, never lost); an unfinished one
 * becomes `quiz-pending` while the reply is still streaming.
 */
export function splitQuizContent(content: string, options: { streaming?: boolean } = {}): QuizSegment[] {
  const segments: QuizSegment[] = [];
  let rest = content;
  const pushText = (text: string) => {
    if (text.trim()) segments.push({ type: 'text', text });
  };

  for (;;) {
    const start = findFenceStart(rest);
    if (start === -1) break;
    const bodyStart = start + OPEN.length;
    const close = rest.indexOf('```', bodyStart);
    pushText(rest.slice(0, start));

    if (close === -1) {
      if (options.streaming) segments.push({ type: 'quiz-pending' });
      else pushText(rest.slice(start)); // truncated reply: leave it readable
      return segments;
    }
    const quiz = parseQuizBlock(rest.slice(bodyStart, close));
    if (quiz) segments.push({ type: 'quiz', quiz });
    else pushText(rest.slice(start, close + 3));
    rest = rest.slice(close + 3);
  }
  pushText(rest);
  return segments;
}

/** The reply without quiz blocks — for public snapshots and previews where a card cannot be shown. */
export function stripQuizBlocks(content: string): string {
  return splitQuizContent(content, { streaming: false })
    .map((segment) => (segment.type === 'text' ? segment.text : ''))
    .join('')
    .trim();
}

/** `quiz` fence starting at a line beginning, so ```quizzes or inline mentions never match. */
function findFenceStart(text: string): number {
  const pattern = /(^|\n)```quiz[ \t]*(?=\r?\n)/g;
  const match = pattern.exec(text);
  return match ? match.index + match[1]!.length : -1;
}

/** Only options that really belong to the quiz, in the quiz's own order. */
export function sanitizeQuizAnswers(quiz: Quiz, answers: QuizAnswers): QuizAnswers {
  const clean: QuizAnswers = {};
  for (const question of quiz.questions) {
    const chosen = (answers[question.id] ?? []).filter((option) => question.options.includes(option));
    const ordered = question.options.filter((option) => chosen.includes(option));
    const picked = question.multiple ? ordered : ordered.slice(0, 1);
    if (picked.length > 0) clean[question.id] = picked;
  }
  return clean;
}

export function isQuizAnswered(quiz: Quiz, answers: QuizAnswers): boolean {
  return Object.keys(sanitizeQuizAnswers(quiz, answers)).length > 0;
}

/** The user message that carries the answers back to the model. */
export function formatQuizAnswers(quiz: Quiz, answers: QuizAnswers, intro: string): string {
  const clean = sanitizeQuizAnswers(quiz, answers);
  const lines = quiz.questions
    .filter((question) => clean[question.id])
    .map((question, index) => `${index + 1}. ${question.text} — ${clean[question.id]!.join(', ')}`);
  return [intro.trim(), ...lines].filter(Boolean).join('\n');
}
